import React from "react";
import RankChanger from "./RankChanger";
import TextCell from "./gridCells/TextCell";
import TwoLineCell from "./gridCells/TwoLineCell";
import { ActionOnKey } from "../interfaces/ActionOnKey";
import BonusDetailsView from "./BonusDetailsView";
import { RuleModuleType } from "../constants/constants";
import EditRuleItemView from "./forms/EditRuleItemView";
import Alerts from "./Alerts";
import { AlertType } from "../types/AlertTypes";
import { RuleModuleContext } from "../contexts/RuleModuleContext";
import { CharacterHistoryContext } from "../contexts/CharacterHistoryContext";
import { XPSystemContext } from "../contexts/XPSystemContext";
import { CharacterSheetContext } from "../contexts/CharacterSheetContext";
import { BonusBreakdownContext } from "../contexts/BonusBreakdownContext";
import { RuleItemContext } from "../contexts/RuleItemContext";

interface Props {
  moduleTypeOverride?: RuleModuleType;
}

export default ({ moduleTypeOverride }: Props) => {
  const {
    emptyBonusSourceDetailsForItem,
    config,
    levelUpOptions,
    moduleType,
  } = React.useContext(RuleModuleContext);
  const { updateRank } = React.useContext(CharacterHistoryContext);
  const { adminMode, showSources, characterEditMode } = React.useContext(
    CharacterSheetContext
  );
  const { getXPConfigs } = React.useContext(XPSystemContext);
  const { currentBonuses } = React.useContext(BonusBreakdownContext);
  const { key } = React.useContext(RuleItemContext);

  const ruleModuleType = moduleTypeOverride ?? moduleType;

  const itemBreakdown =
    currentBonuses[key] || emptyBonusSourceDetailsForItem(key);
  const onClick = (action: ActionOnKey) =>
    updateRank({
      updateRankAction: action,
      ruleItemConfig: config,
      levelUpOptionsAndConfig: levelUpOptions
        ? { levelUpOptions, xpConfigs: getXPConfigs() }
        : undefined,
    });

  const itemNameId = `item-name-${key}`;
  const elements = [
    <TwoLineCell
      key={itemNameId}
      dataTest={itemNameId}
      lineOne={itemBreakdown.actualBonus.sourceName}
      lineTwo={key}
    />,
  ];
  if (adminMode) {
    elements.push(
      <EditRuleItemView
        key={`edit-item-${key}`}
        itemBreakdown={itemBreakdown}
      />
    );
  } else {
    if (
      ![
        RuleModuleType.ItemOnly,
        RuleModuleType.ItemAndSource,
        RuleModuleType.SelectOnly,
      ].includes(ruleModuleType)
    ) {
      if (characterEditMode) {
        elements.push(
          <RankChanger
            key={`item-rank-${key}`}
            itemKey={key}
            rank={itemBreakdown.rank}
            config={config}
            onClick={onClick}
          />
        );
      } else {
        const rankValueId = `rank-value-${key}`;
        elements.push(
          <TextCell
            key={rankValueId}
            dataTest={rankValueId}
            text={itemBreakdown.rank}
          />
        );
      }
    }
    if (![RuleModuleType.SelectAndRank].includes(ruleModuleType)) {
      const bonusValueId = `bonus-value-${key}`;
      elements.push(
        <TextCell
          key={bonusValueId}
          dataTest={bonusValueId}
          text={itemBreakdown.actualBonus.current}
        />
      );
    }
  }
  if (moduleType !== RuleModuleType.SelectAndRank) {
    if (showSources) {
      elements.push(
        <BonusDetailsView
          thirdColumn={
            ![RuleModuleType.ItemOnly, RuleModuleType.ItemAndSource].includes(
              moduleType
            )
          }
          key={`bonus-sources-${key}`}
          bonusSourceDetails={itemBreakdown}
        />
      );
    }
  }
  elements.push(
    <Alerts key="rule-item-alert" itemKey={key} alertType={AlertType.ITEM} />
  );
  return React.createElement(React.Fragment, {}, elements);
};
